import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  useTheme,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import LinkOffRounded from "@mui/icons-material/LinkOffRounded";
import { authClient } from "../../lib/auth-client";
import { AuthPageLayout, AuthStatusBadge } from "../auth/AuthPageLayout";
import {
  makeGlassButtonSx,
  makeGlassFieldSx,
  primaryButtonSx,
} from "../auth/authStyles";

const ResetPasswordPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const glassFieldSx = makeGlassFieldSx(isDark);
  const glassButtonSx = makeGlassButtonSx(isDark);

  const [token, setToken] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [tokenError, setTokenError] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    const errorParam = searchParams.get("error");

    if (tokenParam) {
      setToken(tokenParam);
    } else if (errorParam) {
      setTokenError(true);
    } else {
      setTokenError(true);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (!token) {
      setTokenError(true);
      return;
    }

    setIsLoading(true);

    try {
      await authClient.resetPassword({
        newPassword: password,
        token,
      });

      // The snackbar provider sits outside the router, so the toast follows
      // the user to the sign-in screen and survives any remounts there.
      enqueueSnackbar("Password reset successful. Sign in with your new password.", {
        variant: "success",
      });
      navigate("/sign-in?method=email");
    } catch (error: any) {
      const errorMessage = error?.message ?? "Failed to reset password";

      if (errorMessage.includes("token") || errorMessage.includes("expired")) {
        setTokenError(true);
      } else if (errorMessage.includes("rate")) {
        setError("Too many attempts. Please try again later.");
      } else {
        setError(errorMessage);
      }

      setIsLoading(false);
    }
  };

  // Missing, invalid or expired token — offer the way back into the flow.
  if (tokenError) {
    return (
      <AuthPageLayout
        contentKey="expired"
        topSlot={
          <AuthStatusBadge tone="error">
            <LinkOffRounded sx={{ fontSize: 34 }} />
          </AuthStatusBadge>
        }
        headline="That link didn't work"
        supporting="Password reset links only work once and expire after a short while. Request a new one and try again."
      >
        <Stack spacing={1.5}>
          <Button fullWidth onClick={() => navigate("/forgot-password")} sx={primaryButtonSx}>
            Request a new link
          </Button>
          <Button fullWidth onClick={() => navigate("/sign-in?method=email")} sx={glassButtonSx}>
            Back to sign in
          </Button>
        </Stack>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout
      contentKey="form"
      headline="Create a new password"
      supporting="Choose a strong password for your account. You'll sign in with it from now on."
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={1.5}>
          <TextField
            fullWidth
            size="small"
            label="New password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
            autoFocus
            helperText={
              password.length > 0 && password.length < 8
                ? "At least 8 characters"
                : undefined
            }
            sx={glassFieldSx}
          />
          <TextField
            fullWidth
            size="small"
            label="Confirm new password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={isLoading}
            error={confirmPassword.length > 0 && confirmPassword !== password}
            helperText={
              confirmPassword.length > 0 && confirmPassword !== password
                ? "Passwords do not match"
                : undefined
            }
            sx={glassFieldSx}
          />
          <Box sx={{ height: 8 }} />
          <Button fullWidth type="submit" disabled={isLoading} sx={primaryButtonSx}>
            {isLoading && <CircularProgress size={24} color="inherit" />}
            {!isLoading && "Reset password"}
          </Button>
          <Box sx={{ pt: 1 }}>
            <Button
              fullWidth
              type="button"
              onClick={() => navigate("/sign-in?method=email")}
              disabled={isLoading}
              sx={glassButtonSx}
            >
              Back to sign in
            </Button>
          </Box>
        </Stack>
      </form>
    </AuthPageLayout>
  );
};

export default ResetPasswordPage;
